import * as Sentry from "@sentry/react";

let sentryInitialized = false;

/**
 * Initialize Sentry for error tracking and performance monitoring.
 * Should only be called after user has given consent for analytics.
 */
export function initSentry(): void {
    if (sentryInitialized) {
        return;
    }

    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (!dsn) {
        console.warn("Sentry DSN not configured");
        return;
    }

    Sentry.init({
        dsn,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",

        // Do not send default PII (IP addresses, cookies, etc.)
        sendDefaultPii: false,

        // Performance Monitoring - sample 10% of transactions
        tracesSampleRate: 0.1,

        // Integrations
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],

        // Capture Replay for 10% of all sessions,
        // plus 100% of sessions with an error
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,

        // Filter out sensitive data from breadcrumbs and events
        beforeSend(event) {
            // Remove any potentially sensitive query parameters
            if (event.request?.url) {
                try {
                    const url = new URL(event.request.url);
                    const sensitiveParams = ['token', 'password', 'api_key', 'secret'];
                    sensitiveParams.forEach(param => url.searchParams.delete(param));
                    event.request.url = url.toString();
                } catch {
                    // Invalid URL, leave as is
                }
            }
            return event;
        },
    });

    sentryInitialized = true;
}

/**
 * Set user context in Sentry (ID only for privacy).
 * Call this after user authentication.
 */
export function setSentryUser(userId: number | null): void {
    if (!sentryInitialized) {
        return;
    }

    if (userId) {
        Sentry.setUser({ id: userId.toString() });
    } else {
        Sentry.setUser(null);
    }
}

/**
 * Check if Sentry has been initialized
 */
export function isSentryInitialized(): boolean {
    return sentryInitialized;
}
